import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from './supabase'
import { loadCachedSession, saveCachedSession, clearCachedSession } from './sessionCache'
import { restGet } from './rest'
import type { User } from './types'
import { publicOrigin } from '@/lib/platform'

// Auth state machine. Every state is a discrete reason for what the UI
// should show — no ambiguous "session exists but user is null and we don't
// know why" any more.
//
//   'init'    — first paint, getSession() hasn't returned yet
//   'public'  — no session: show Login / Signup
//   'loading' — session present, loading user row (with retry budget)
//   'ready'   — session + user loaded, app can run
//   'missing' — server confirmed: no users row for this auth user (setup repair)
//   'error'   — terminal failure loading user row (network/auth): show error + retry
export type AuthPhase = 'init' | 'public' | 'loading' | 'ready' | 'missing' | 'error'

interface AuthState {
  phase: AuthPhase
  session: Session | null
  user: User | null
  errorMessage: string | null
  // Becomes true when the user-row fetch has been retrying for >3s, so the
  // UI can show a clearer "still connecting — Supabase is waking up" hint
  // rather than just "Loading…"
  takingLong: boolean
  recoveryMode: boolean
  clearRecoveryMode: () => void
  retry: () => Promise<void>
  signIn: (email: string, password: string) => Promise<{ error?: string }>
  signInMagic: (email: string) => Promise<{ error?: string }>
  signOut: () => Promise<void>
  refresh: () => Promise<void>
  // Backward-compat shims for the rest of the app.
  loading: boolean
  userMissing: boolean
}

const AuthCtx = createContext<AuthState | null>(null)

// Total worst-case wait: ~31s. Supabase free-tier cold-start can take ~25s
// after idle, so the retry budget needs to outlast it. We pair this with a
// visible "still connecting" message after a few seconds so the user knows
// we're working, not stuck.
const RETRY_BUDGET = { tries: 6, baseMs: 500 }

async function retry<T>(
  fn: () => Promise<T>,
  opts = RETRY_BUDGET,
  onAttempt?: (attemptIdx: number) => void,
): Promise<T> {
  let lastErr: any
  for (let i = 0; i < opts.tries; i++) {
    onAttempt?.(i)
    try { return await fn() }
    catch (e) {
      lastErr = e
      const wait = opts.baseMs * Math.pow(2, i) // 0.5, 1, 2, 4, 8, 16
      await new Promise(r => setTimeout(r, wait))
    }
  }
  throw lastErr
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<AuthPhase>('init')
  const [session, setSession] = useState<Session | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [takingLong, setTakingLong] = useState(false)
  const [recoveryMode, setRecoveryMode] = useState(false)
  // Mirror of phase, kept in a ref so watchdog setTimeouts can read the
  // LATEST phase rather than the stale captured value at effect-mount time.
  // The previous bug: watchdog set at T0 captured phase='init', then even
  // after init succeeded and phase became 'ready', the watchdog still saw
  // 'init' in its closure and forced 'error' 10s later.
  const phaseRef = useRef<AuthPhase>('init')
  useEffect(() => { phaseRef.current = phase }, [phase])

  // ----- Load the users row, distinguishing three outcomes -----
  //
  // A bare `await supabase.from(...).select(...)` can hang INDEFINITELY when
  // the underlying fetch never settles (mid-token-refresh deadlock, dead
  // socket the SW is holding open, blocked-by-extension, etc.). When that
  // happens, the retry() wrapper can't help — its `await fn()` never returns,
  // no retry kicks in, no error fires, and the user is stuck on the loading
  // screen FOREVER with only "watchdog tripped" warnings (which only flip us
  // to public if phase is still 'init' — once loadFor has started, those
  // warnings are useless).
  //
  // Fix: race every individual fetch against an 8-second timeout. A reject
  // lets retry() back off and try again, and if all retries time out we
  // finally land in the 'error' phase with a real message + retry button.
  async function fetchUserRow(authUserId: string): Promise<User | 'missing'> {
    // Raw REST (not supabase-js) — the supabase-js client wedges in long-
    // lived browser sessions and this exact query has been the #1 source
    // of "stuck on Loading" reports.
    const data = await restGet<User | null>('users', {
      select: '*',
      filters: [`id=eq.${authUserId}`],
      single: true,
      timeoutMs: 8000,
      label: 'user row fetch',
    })
    if (data == null) return 'missing'
    return data
  }

  // Drive the state machine for a given session. Public-only sessions short-
  // circuit; sessions with a user kick off the resilient load.
  async function loadFor(sess: Session | null) {
    if (!sess?.user) {
      setSession(null); setUser(null); setErrorMessage(null); setPhase('public')
      return
    }
    setSession(sess)

    // OPTIMISTIC SHELL — synthesise a minimal user from the JWT and render
    // the app immediately. Real user-row fetch refreshes in background.
    // Why: even when the React Query and sessionCache caches are cold, the
    // JWT already has everything App.tsx needs to gate routes (id,
    // tenant_id, role from app_metadata). Spinning on the user-row fetch
    // for up to 30s when REST is cold was the last remaining "Loading…"
    // hang. With this, the app shell is up the moment a valid session
    // exists — DB just fills in full_name/created_at later.
    const cached = loadCachedSession(sess.user.id)
    if (cached?.user) {
      setUser(cached.user)
      setPhase('ready')
    } else {
      const md = (sess.user.app_metadata || {}) as Record<string, unknown>
      const tenantId = typeof md.tenant_id === 'string' ? md.tenant_id : ''
      const role = (typeof md.role === 'string' ? md.role : 'snagger') as User['role']
      // Only show the optimistic shell if we have what App.tsx really needs.
      if (tenantId) {
        setUser({
          id: sess.user.id,
          tenant_id: tenantId,
          email: sess.user.email || '',
          full_name: (sess.user.user_metadata?.full_name as string | undefined) || null,
          role,
          created_at: sess.user.created_at || new Date().toISOString(),
        })
        setPhase('ready')
      } else {
        setPhase('loading')
      }
    }

    // Flip the "still connecting" message on after 3 seconds of retries.
    const longTimer = setTimeout(() => setTakingLong(true), 3000)
    try {
      const result = await retry(() => fetchUserRow(sess.user.id))
      clearTimeout(longTimer); setTakingLong(false)
      if (result === 'missing') {
        setUser(null); clearCachedSession(); setPhase('missing')
      } else {
        setUser(result); setErrorMessage(null); setPhase('ready')
        // Signal to main.tsx so it can lazy-boot Sentry/PostHog. We don't
        // load those on the public sign-in page (where they have no user
        // to attribute events to anyway) — saves ~120KB on cold-start.
        if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('ss:user-signed-in'))
      }
    } catch (e: any) {
      clearTimeout(longTimer); setTakingLong(false)
      // Background failure. If we have a cached user, stay on 'ready' so the
      // app keeps working — we'll quietly retry on next nav. If we don't,
      // surface the error so the user can do something about it.
      console.warn('[auth] user row load failed', e)
      if (cached?.user) {
        // Already on 'ready' from above, just leave it.
      } else {
        setErrorMessage(e?.message || 'Could not reach the server.')
        setPhase('error')
      }
    }
  }

  useEffect(() => {
    let cancelled = false

    function init() {
      // OPTIMISTIC LOCAL-FIRST INIT — skip supabase.auth.getSession() entirely.
      // It was causing 10s deadlocks (internal auth lock interaction with
      // autoRefreshToken), and worse, my previous "clear local auth on
      // failure" fix was nuking valid sessions on every page-load that hit
      // the deadlock. New approach:
      //
      //   1. Read the cached session DIRECTLY from localStorage. If it's
      //      present and not expired, treat the user as signed in
      //      immediately — render the app from the cached user (instant).
      //   2. supabase.auth.onAuthStateChange below picks up the same
      //      localStorage session and fires INITIAL_SESSION, which calls
      //      loadFor() to refresh the user/tenant rows in the background.
      //   3. If reading localStorage gives us nothing (or an expired
      //      token), fall through to 'public' immediately — no spinner,
      //      no error.
      //
      // Net effect: cold-boot with a valid session goes from up to 10 s
      // of "Loading…" to instant. The session is NEVER cleared from
      // localStorage on a perceived failure — only on a real SIGNED_OUT.
      try {
        const projectRef = (import.meta.env.VITE_SUPABASE_URL || '').match(/https:\/\/([^.]+)\./)?.[1]
        const key = projectRef ? `sb-${projectRef}-auth-token` : null
        const raw = key ? localStorage.getItem(key) : null
        if (raw) {
          const parsed = JSON.parse(raw)
          const expiresAt: number | undefined = parsed?.expires_at
          const nowSec = Math.floor(Date.now() / 1000)
          if (parsed?.access_token && expiresAt && expiresAt > nowSec + 30) {
            // Valid session in storage — render from it immediately.
            // loadFor will refresh the user row in the background; if
            // there's a cached user, it sets phase='ready' synchronously.
            loadFor(parsed as Session)
            return
          }
        }
        // No usable session — straight to public.
        if (!cancelled) setPhase('public')
      } catch (e) {
        // localStorage threw (private mode etc.) — just show Login.
        console.warn('[auth] local session read failed — showing Login', e)
        if (!cancelled) setPhase('public')
      }
    }
    init()

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, sess) => {
      if (cancelled) return
      if (event === 'PASSWORD_RECOVERY') setRecoveryMode(true)
      if (event === 'SIGNED_OUT') {
        setRecoveryMode(false); clearCachedSession()
        setSession(null); setUser(null); setPhase('public'); setErrorMessage(null)
        return
      }
      // TOKEN_REFRESHED fires roughly every hour as Supabase rotates the
      // access token. Don't re-run the full user-row fetch — just absorb
      // the new session so subsequent API calls use the fresh token. This
      // was previously causing transient failures to flip the whole app
      // into the error shell while the user was just looking at a list.
      if (event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        if (sess) setSession(sess)
        return
      }
      // For SIGNED_IN and INITIAL_SESSION, run the proper resilient load.
      await loadFor(sess)
    })

    return () => { cancelled = true; subscription.unsubscribe() }
  /* eslint-disable-next-line */
  }, [])

  // Persist whatever's current to the cache so the NEXT boot is instant.
  useEffect(() => {
    if (session?.user && user) saveCachedSession(session.user.id, user, null)
  }, [session?.user, user])

  async function signIn(email: string, password: string) {
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password })
    if (error) return { error: friendlyAuthError(error.message) }
    return {}
  }
  async function signInMagic(email: string) {
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { emailRedirectTo: publicOrigin() },
    })
    if (error) return { error: friendlyAuthError(error.message) }
    return {}
  }
  async function signOut() {
    clearCachedSession()
    await supabase.auth.signOut().catch(() => {})
    setSession(null); setUser(null); setErrorMessage(null); setPhase('public')
  }
  async function refresh() {
    if (!session) return
    await loadFor(session)
  }
  async function retryFn() {
    setPhase('loading'); setErrorMessage(null)
    const { data } = await supabase.auth.getSession()
    await loadFor(data.session)
  }

  const loading = phase === 'init' || phase === 'loading'
  const userMissing = phase === 'missing'

  return (
    <AuthCtx.Provider value={{
      phase, session, user, errorMessage, takingLong, recoveryMode,
      clearRecoveryMode: () => setRecoveryMode(false),
      retry: retryFn,
      signIn, signInMagic, signOut, refresh,
      loading, userMissing,
    }}>
      {children}
    </AuthCtx.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthCtx)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}

function friendlyAuthError(message: string) {
  if (!message) return 'Sign-in failed. Please try again.'
  if (/invalid login credentials/i.test(message)) return 'Wrong email or password.'
  if (/email not confirmed/i.test(message)) return 'Confirm your email first — check your inbox.'
  if (/rate limit/i.test(message)) return 'Too many tries. Wait a minute and try again.'
  return message
}
